// Create a shared FormHelper component
import { IconInfo, IconWarning } from "@teral/icons";
// biome-ignore lint/style/useImportType: Reqired for silencing Biome
import React from "react";
import { cn } from "../../lib/utils";
import { InfoTooltip } from "../info-tooltip";
import { OptionalTag, RequiredTag } from "./form-tags";
export { DocsTag, OptionalTag, RequiredTag } from "./form-tags";

export type FormHelperProps = {
  description?: string | React.ReactNode;
  error?: string;
  variant?: string | null;
  descriptionId: string;
  errorId: string;
};

export const FormDescription = ({
  description,
  error,
  variant,
  descriptionId,
  errorId,
}: FormHelperProps) => {
  if (!description && !error) {
    return null;
  }

  return (
    <div className="text-sm leading-5">
      {error ? (
        <div id={errorId} role="alert" className="text-error-11 flex gap-2 items-center">
          <IconWarning className="size-3.5 shrink-0 -ml-px mr-px" aria-hidden="true" />
          <span className="flex-1">{error}</span>
        </div>
      ) : description ? (
        <output
          id={descriptionId}
          className={cn(
            "text-gray-9 flex gap-2 items-start",
            variant === "success"
              ? "text-success-11"
              : variant === "warning"
                ? "text-warning-11"
                : "",
          )}
        >
          {variant === "warning" ? (
            <IconWarning className="size-3.5 shrink-0 mt-[3px]" aria-hidden="true" />
          ) : (
            <IconInfo className="size-3.5 shrink-0 mt-[3px]" aria-hidden="true" />
          )}
          <span
            className={cn("flex-1", variant === "warning" ? "text-warning-11" : "text-gray-10")}
          >
            {description}
          </span>
        </output>
      ) : null}
    </div>
  );
};

export type Requirement = "required" | "optional";

export type FormLabelProps = {
  label?: string;
  requirement?: Requirement;
  hasError?: boolean;
  htmlFor: string;
  tooltipContent?: string | React.ReactNode;
};

export const FormLabel = ({
  label,
  requirement,
  hasError,
  htmlFor,
  tooltipContent,
}: FormLabelProps) => {
  if (!label) {
    return null;
  }

  return (
    <label
      id={`${htmlFor}-label`}
      htmlFor={htmlFor}
      className="text-gray-11 text-sm flex items-center"
    >
      {label}
      {requirement === "required" && <RequiredTag hasError={hasError} />}
      {requirement === "optional" && <OptionalTag />}
      {tooltipContent && (
        <InfoTooltip content={tooltipContent} asChild>
          <span className="ml-1.5 inline-flex items-center text-gray-9 hover:text-gray-11">
            <IconInfo className="size-3.5" aria-hidden="true" />
            <span className="sr-only">More info</span>
          </span>
        </InfoTooltip>
      )}
    </label>
  );
};
